'use client'

import React from 'react'
import { ChevronDown, PlusCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useCart, useModals } from '@/lib/hooks'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Separator,
} from '@/components/ui'

interface AddCartDiscountProps extends React.ComponentPropsWithoutRef<'form'> {
  cartId: string
}

export const AddCartDiscount = ({ cartId }: AddCartDiscountProps) => {
  const { addCartDiscount } = useCart() as any
  const { closeModal } = useModals()
  const [discountType, setDiscountType] = React.useState<string>('fixed')
  const [discount, setDiscount] = React.useState<number>()

  const handleSubmit = async (data: FormData) => {
    const discount = Number(data.get('discount'))

    if (!cartId) {
      return toast.error('Cart ID is missing.')
    }

    if (discount < 0) {
      return toast.error('Discount cannot be negative.')
    }

    try {
      addCartDiscount({
        cartId,
        discount: discount,
        discountType: discountType,
      })
      toast.success('Cart item updated successfully.')
      closeModal('ADD_CART_DISCOUNT')
    } catch (error) {
      toast.error('Failed to update cart item.')
    }
  }

  return (
    <form
      className="flex w-full flex-wrap gap-4 lg:gap-8"
      action={handleSubmit}
    >
      <div className="flex w-full flex-col flex-wrap gap-4">
        <h2 className="text-balance text-xl font-medium">Add Cart Discount</h2>

        <div className="flex w-full flex-col gap-5">
          <div className="flex w-full flex-col gap-5">
            <div className="flex w-full items-center gap-2">
              <Label className="shrink-0 !text-sm font-medium text-gray-700">
                Discount per Item
              </Label>
              <Separator className="mt-1" />
            </div>

            <div className="flex items-center gap-2">
              <Input
                className="w-full appearance-none text-center"
                value={discount?.toString()}
                inputSize="lg"
                name="discount"
                onChangeValue={(value) => setDiscount(Number(value))}
                type="number"
                placeholder="0"
              />
              <div className="flex w-full items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full justify-between"
                    >
                      <span>
                        {discountType === 'fixed'
                          ? '(Rs) Fixed Amount'
                          : '(%) Percentage'}
                      </span>
                      <ChevronDown className="size-5" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
                    <div className="flex w-full flex-col gap-0.5 bg-gray-50 p-1">
                      <DropdownMenuItem className="w-full" asChild>
                        <Button
                          variant="outline"
                          className="w-full rounded-sm shadow-none"
                          onClick={() => setDiscountType('fixed')}
                        >
                          (Rs) Fixed Amount
                        </Button>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="w-full" asChild>
                        <Button
                          variant="outline"
                          className="w-full rounded-sm shadow-none"
                          onClick={() => setDiscountType('percentage')}
                        >
                          (%) Percentage
                        </Button>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full gap-2 pt-3">
          <Button
            variant="default"
            className="w-full gap-3 shadow-card"
            size="lg"
            type="submit"
          >
            <PlusCircle className="size-5" strokeWidth={1.5} />
            <span>Add Discount</span>
          </Button>
        </div>
      </div>
    </form>
  )
}
